import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Logo from '../../assets/Images/Logo.png';
import Navbar from '../../Components/Navbar';
import api from '../../api/axiosConfig';
import { useTranslation } from 'react-i18next';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t, i18n } = useTranslation('ForgotPassword');

  useEffect(() => {
    console.log('CURRENT LANGUAGE:', i18n.language);
    console.log('TEST TRANSLATION:', t('forgotPassword.title'));
  }, [i18n.language, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      const response = await api.post('/forgot-password/', { email });
      const data = response.data;

      if (data.success) {
        setMessage(data.message || t('forgotPassword.messages.success'));
      } else {
        setError(data.error || t('forgotPassword.messages.error.reset'));
      }
    } catch (err) {
      console.error('Full error:', err);
      console.error('Response data:', err.response?.data);

      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.status === 403) {
        setError('Security error. Please refresh and try again.');
      } else {
        setError(err.message || t('forgotPassword.messages.error.generic'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-purple-900 via-black to-purple-900">
      <div className="absolute top-0 left-0 w-full">
        <Navbar minimal />
      </div>

      <div className="bg-[var(--bg-black)] p-8 rounded-2xl shadow-xl w-full border border-solid border-white/40 max-w-md text-center">
        <div className="flex justify-center bg-transparent">
          <img src={Logo} alt="Logo" className="h-12 w-12 bg-transparent" />
        </div>

        <h2 className="text-2xl font-bold text-[var(--text-black)] mb-4">
          {t('forgotPassword.title')}
        </h2>

        <p className="text-gray-400 mb-6 text-sm">
          {t('forgotPassword.description')}
        </p>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-green-900 text-green-100 rounded-lg text-sm"
          >
            {message}
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-red-900 text-red-100 rounded-lg text-sm"
          >
            {error}
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            placeholder={t('forgotPassword.form.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-2 bg-[var(--bg-black)] text-[var(--text-black)] rounded-lg border-[var(--border)] border focus:outline-none focus:ring-2 focus:ring-pink-500"
          />

          <motion.button
            animate={{
              background: [
                'linear-gradient(90deg, #FF1493, #FF69B4)',
                'linear-gradient(90deg, #FF6347, #FF1493)',
              ],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
            className="w-full text-white py-2 rounded-lg text-lg font-semibold border-none flex justify-center items-center"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t('forgotPassword.form.submitting')}
              </span>
            ) : (
              t('forgotPassword.form.submit')
            )}
          </motion.button>
        </form>

        <div className="mt-6">
          <Link
            to="/login"
            className="text-pink-500 text-sm font-medium hover:underline"
          >
            ← {t('forgotPassword.navigation.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
